import React from "react";
import { useEtatGlobal } from "../EtatGlobal.js";
import { useClients } from "../Portefeuille/useClients.js";
import { Icon, PrIco, ProcAv } from "../Procedures/ProcUi";
import { procFmt, procNorm } from "../Procedures/procHelpers";
import { procName, procSetPeople, procWho } from "../Procedures/procPeople";
import "../Procedures/Procedures.css";
import "./KnowledgeBase.css";
import { kbDom, kbFolderText, kbHi, kbSnip } from "./kbHelpers";
import { KbBadge, KbFolder } from "./KbFolder";
import { KbNewModal } from "./KbNewModal";
import { useKbStore } from "./useKbStore";
import { rechercheIntelligenteApi } from "./kbApi";
import { ProcAssistant, ProcMascot } from "../Procedures/ProcAssistant";

// Base de connaissance — dossiers de recherche du cabinet (documentation
// métier). Portage de l'écran « Base de connaissance » de la maquette Claude
// Design (kb.jsx). Données lues et écrites via l'API ; l'assistant IA et la
// recherche intelligente interrogent le backend (recherche dans la base +
// Claude) au lieu d'envoyer toute la base depuis le navigateur.
const KnowledgeBase = () => {
  const { users, utilisateur } = useEtatGlobal();
  const { data: clients = [] } = useClients();
  procSetPeople(users, utilisateur);

  const [db, api, etat] = useKbStore();
  const clientsParId = React.useMemo(() => new Map(clients.map((c) => [c.id, c])), [clients]);
  const nomClient = (id) => (clientsParId.get(id) || {}).nom || "";
  const [view, setView] = React.useState({ t: "list" });
  const [q, setQ] = React.useState("");
  const [sort, setSort] = React.useState("updated");
  const [newOpen, setNewOpen] = React.useState(false);
  const [toast, setToast] = React.useState(null);
  const [ai, setAi] = React.useState(false);
  const [aiAsk, setAiAsk] = React.useState(null);
  const [smart, setSmart] = React.useState(null);
  const toastTimer = React.useRef(null);

  const flash = (m) => {
    setToast(m);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 2400);
  };
  React.useEffect(() => () => clearTimeout(toastTimer.current), []);

  const folder = view.t === "folder" ? db.folders.find((f) => f.id === view.id) : null;
  // Dossier supprimé (ou introuvable) : retour à la liste.
  React.useEffect(() => {
    if (view.t === "folder" && !folder) setView({ t: "list" });
  }, [view, folder]);
  const open = (id) => setView({ t: "folder", id });
  const askAi = (question) => {
    setAi(true);
    setAiAsk({ q: question, n: Date.now() });
  };

  const myFavs = db.favs.filter((id) => db.folders.some((f) => f.id === id));
  const isFav = (id) => myFavs.includes(id);
  const qt = q.trim();

  const scored = db.folders
    .map((f) => {
      if (!qt) return { f, score: 0 };
      const nq = procNorm(qt);
      const words = nq.split(/\s+/).filter((w) => w.length > 1);
      const txt = procNorm(kbFolderText(f, nomClient));
      const tt = procNorm(f.title);
      if (!words.every((w) => txt.includes(w))) return null;
      return {
        f,
        score:
          (tt.includes(nq) ? 10 : 0) +
          words.filter((w) => tt.includes(w)).length * 3 +
          f.tags.filter((t) => words.some((w) => procNorm(t).includes(w))).length * 2 +
          1,
      };
    })
    .filter(Boolean)
    .sort((a, b) => b.score - a.score);

  const runSmart = async () => {
    setSmart({ busy: true, q: qt });
    try {
      const r = await rechercheIntelligenteApi(qt);
      setSmart({ q: qt, list: (r.resultats || []).filter((x) => db.folders.some((f) => f.id === x.id)) });
    } catch (e) {
      console.error("Recherche intelligente :", e);
      setSmart({ q: qt, err: true });
    }
  };
  React.useEffect(() => {
    if (smart && smart.q !== qt) setSmart(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qt]);

  const sortBy = (key) => setSort((s) => (s === key ? key + "-r" : key));
  const base = view.t === "favs" ? scored.filter((x) => isFav(x.f.id)) : scored;
  const rows = qt
    ? base
    : [...base].sort((x, y) => {
        const [key, r] = sort.split("-");
        const m = r ? -1 : 1;
        if (key === "az") return m * x.f.title.localeCompare(y.f.title, "fr");
        if (key === "created") return m * (y.f.created - x.f.created);
        return m * (y.f.updated - x.f.updated);
      });

  const Th = ({ k: key, children }) => (
    <button className={`kb-th ${sort.startsWith(key) && !qt ? "on" : ""}`} onClick={() => sortBy(key)}>
      {children}
      {sort.startsWith(key) && !qt && (
        <Icon name="chevDown" size={12} style={{ transform: sort.endsWith("-r") ? "rotate(180deg)" : "" }} />
      )}
    </button>
  );

  const List = () => (
    <div className="kb-page kb-list">
      {view.t === "favs" && (
        <div className="pr-home-intro kb-intro">
          <h1>Favoris</h1>
          <p>Vos dossiers épinglés · {myFavs.length}</p>
        </div>
      )}
      <div className="kb-search">
        <Icon name="search" size={18} />
        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Rechercher dans toute la base : titres, synthèses, sources, documents…"
          onKeyDown={(e) => {
            if (e.key === "Enter" && qt.length > 2) {
              if (/\?\s*$/.test(qt)) askAi(qt);
              else runSmart();
            }
          }}
        />
        {q && (
          <button className="kb-search-x" onClick={() => setQ("")}>
            <Icon name="x" size={14} />
          </button>
        )}
        <button className="kb-search-ai" disabled={qt.length < 3} onClick={() => askAi(qt)} title="Poser la question à l'assistant">
          <PrIco n="spark" s={14} /> Demander à l'IA
        </button>
      </div>
      {qt.length > 2 && (
        <div className="kb-smart">
          {!smart && (
            <button className="kb-smart-btn" onClick={runSmart}>
              <PrIco n="spark" s={14} /> Recherche intelligente : trouver aussi les dossiers liés par le sens
            </button>
          )}
          {smart && smart.busy && (
            <div className="kb-smart-busy">
              <span className="pr-ai-wait"><span></span><span></span><span></span></span> L'IA parcourt la base…
            </div>
          )}
          {smart && smart.err && (
            <div className="pr-ai-err"><Icon name="alert" size={14} /> La recherche intelligente n'a pas abouti. Réessayez.</div>
          )}
          {smart && smart.list && (
            <div className="kb-smart-res">
              <div className="kb-smart-h"><PrIco n="spark" s={13} /> Suggestions de l'IA</div>
              {smart.list.length ? (
                smart.list.map((x) => {
                  const f = db.folders.find((y) => y.id === x.id);
                  return (
                    <button key={x.id} className="kb-smart-i" onClick={() => open(f.id)}>
                      <Icon name="folder" size={15} />
                      <span><b>{f.title}</b><small>{x.raison}</small></span>
                    </button>
                  );
                })
              ) : (
                <div className="kb-smart-none">
                  Aucun dossier lié. <button className="pr-link" onClick={() => askAi(qt)}>Poser la question à l'assistant</button> ou{" "}
                  <button className="pr-link" onClick={() => setNewOpen(qt)}>créer un dossier</button>.
                </div>
              )}
            </div>
          )}
        </div>
      )}
      {etat.erreur && (
        <div className="kb-err" style={{ margin: "14px 0 0" }}>
          Impossible de charger la base de connaissance ({etat.erreur}).{" "}
          <button className="pr-link" onClick={etat.recharger}>Réessayer</button>
        </div>
      )}
      <div className="kb-table">
        <div className="kb-tr kb-thead">
          <Th k="az">Dossier</Th>
          <span className="kb-th">Validation</span>
          <Th k="created">Création</Th>
          <Th k="updated">Modification</Th>
          <span className="kb-th kb-c-n">Éléments</span>
          <span></span>
        </div>
        {etat.chargement && !db.folders.length &&
          Array.from({ length: 4 }, (_, i) => (
            <div key={i} className="kb-tr kb-skel" aria-hidden="true">
              <span className="kb-c-main">
                <span className="kb-fold sm kb-skel-b" />
                <span className="kb-c-tt">
                  <span className="kb-skel-b" style={{ width: "62%", height: 12 }} />
                  <span className="kb-skel-b" style={{ width: "38%", height: 10, marginTop: 6 }} />
                </span>
              </span>
              <span><span className="kb-skel-b" style={{ width: 110, height: 18, borderRadius: 9 }} /></span>
              <span><span className="kb-skel-b" style={{ width: 80, height: 12 }} /></span>
              <span><span className="kb-skel-b" style={{ width: 80, height: 12 }} /></span>
              <span><span className="kb-skel-b" style={{ width: 20, height: 12 }} /></span>
              <span />
            </div>
          ))}
        {rows.map(({ f }) => {
          const d = kbDom(f.domain);
          const snip = qt && !procNorm(f.title).includes(procNorm(qt)) ? kbSnip(kbFolderText(f, nomClient), qt.split(/\s+/)[0]) : null;
          return (
            <button key={f.id} className="kb-tr" onClick={() => open(f.id)} style={{ "--dc": d.color }}>
              <span className="kb-c-main">
                <span className="kb-fold sm"><Icon name="folder" size={16} /></span>
                <span className="kb-c-tt">
                  <span className="kb-c-t">{kbHi(f.title, qt)}</span>
                  <span className="kb-c-s">
                    {snip || (
                      <>
                        {d.name}
                        {f.tags.length > 0 && " · " + f.tags.slice(0, 4).map((t) => "#" + t).join(" ")}
                      </>
                    )}
                  </span>
                </span>
              </span>
              <span><KbBadge v={f.validation} /></span>
              <span className="kb-c-d">
                <b>{procFmt(f.created, false)}</b>
                <small><ProcAv id={f.author} size={16} /> {procName(f.author)}</small>
              </span>
              <span className="kb-c-d">
                <b>{procFmt(f.updated, false)}</b>
                <small><ProcAv id={f.updatedBy || f.author} size={16} /> {procName(f.updatedBy || f.author)}</small>
              </span>
              <span className="kb-c-n">{f.items.length}</span>
              <span
                role="button"
                tabIndex={0}
                className={`kb-star ${isFav(f.id) ? "on" : ""}`}
                title={isFav(f.id) ? "Retirer des favoris" : "Épingler en favori"}
                onClick={(e) => { e.stopPropagation(); api.toggleFav(f.id); }}
              >
                <PrIco n="star" s={15} />
              </span>
            </button>
          );
        })}
        {!rows.length && !etat.chargement && view.t === "favs" && !qt && (
          <div className="kb-empty"><p>Aucun favori. Épinglez un dossier avec l'étoile.</p></div>
        )}
        {!rows.length && !etat.chargement && !(view.t === "favs" && !qt) && (
          <div className="kb-empty">
            <p>{qt ? <>Aucun dossier ne contient « {qt} ».</> : "La base est vide."}</p>
            <div className="kb-empty-a">
              {qt && (
                <button className="btn" onClick={() => askAi(qt)}>
                  <PrIco n="spark" s={14} /> Demander à l'IA
                </button>
              )}
              <button className="btn primary" onClick={() => setNewOpen(qt || true)}>
                <Icon name="plus" size={14} /> {qt ? <>Créer le dossier « {qt} »</> : "Créer un dossier"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const sorted = [...db.folders].sort((x, y) => x.title.localeCompare(y.title, "fr"));
  const Side = () => (
    <aside className="pr-side">
      <div className="pr-side-head">
        <span className="pr-side-logo"><Icon name="folder" size={18} /></span>
        <div>
          <div className="pr-side-t">Base de connaissance</div>
          <div className="pr-side-s">Recherches du cabinet</div>
        </div>
        <button className="pr-new-plus" title="Nouveau dossier" aria-label="Nouveau dossier" onClick={() => setNewOpen(true)}>
          <Icon name="plus" size={17} />
        </button>
      </div>
      <nav className="pr-nav">
        <button className={`pr-nav-i ${view.t === "list" ? "on" : ""}`} onClick={() => setView({ t: "list" })}>
          <PrIco n="home" s={15} /> Accueil<span className="pr-nav-n">{db.folders.length}</span>
        </button>
        <button className={`pr-nav-i ${view.t === "activity" ? "on" : ""}`} onClick={() => setView({ t: "activity" })}>
          <PrIco n="activity" s={15} /> Activité
        </button>
        <button className={`pr-nav-i ${view.t === "favs" ? "on" : ""}`} onClick={() => setView({ t: "favs" })}>
          <PrIco n="star" s={15} /> Favoris<span className="pr-nav-n">{myFavs.length}</span>
        </button>
      </nav>
      {myFavs.length > 0 && (
        <>
          <div className="pr-tree-h">Épinglés</div>
          <div className="kb-tree">
            {myFavs
              .map((id) => db.folders.find((f) => f.id === id))
              .map((f) => (
                <button key={f.id} className={`pr-tpg ${folder && folder.id === f.id ? "on" : ""}`} onClick={() => open(f.id)} title={f.title}>
                  <span className="kb-tstar"><PrIco n="star" s={11} /></span>
                  <span className="pr-tpg-t">{f.title}</span>
                </button>
              ))}
          </div>
        </>
      )}
      <div className="pr-tree-h">Dossiers</div>
      <div className="pr-tree kb-tree">
        {sorted.map((f) => (
          <button key={f.id} className={`pr-tpg ${folder && folder.id === f.id ? "on" : ""}`} onClick={() => open(f.id)} title={f.title}>
            <span className="pr-tpg-t">{f.title}</span>
            {f.validation && (
              <span className="kb-tv" title="Validé par un E-C"><Icon name="shieldCheck" size={12} /></span>
            )}
          </button>
        ))}
        {!sorted.length && <div className="pr-empty">Aucun dossier.</div>}
      </div>
    </aside>
  );

  const Activity = () => {
    const groups = [];
    db.log.forEach((e) => {
      const key = new Date(e.date).toDateString();
      let g = groups.find((x) => x.k === key);
      if (!g) groups.push((g = { k: key, d: e.date, items: [] }));
      g.items.push(e);
    });
    const dayLab = (d) => {
      const t = new Date();
      t.setHours(0, 0, 0, 0);
      const x = new Date(d);
      x.setHours(0, 0, 0, 0);
      const n = (t - x) / 86400000;
      return n === 0
        ? "Aujourd'hui"
        : n === 1
          ? "Hier"
          : x.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
    };
    const lab = {
      create: "a créé", edit: "a modifié", item: "a complété", del: "a modifié",
      validate: "a validé", unvalidate: "a levé la validation de", delete: "a supprimé",
    };
    return (
      <div className="kb-page kb-list">
        <div className="pr-home-intro kb-intro">
          <h1>Activité</h1>
          <p>Journal des créations, modifications et validations par auteur</p>
        </div>
        {groups.map((g) => (
          <section key={g.k} className="pr-day">
            <div className="pr-day-h">{dayLab(g.d)}</div>
            <div className="pr-box pr-box-flush">
              {g.items.map((e) => {
                const ok = e.fid && db.folders.some((f) => f.id === e.fid);
                return (
                  <button key={e.id} className="pr-feed" disabled={!ok} onClick={() => ok && open(e.fid)}>
                    <span className="pr-feed-time">
                      {new Date(e.date).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                    </span>
                    <ProcAv id={e.author} size={28} />
                    <span className="pr-feed-main">
                      <span className="pr-feed-l">
                        <b>{procWho(e.author).name}</b> {lab[e.type] || "a modifié"} <span className="pr-feed-t">{e.title}</span>
                      </span>
                      <span className="pr-feed-s">{e.detail || (e.type === "create" ? "Création du dossier" : "")}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </section>
        ))}
        {!groups.length && <div className="pr-empty">Aucune activité.</div>}
      </div>
    );
  };

  return (
    <div className={`pr kb ${ai ? "has-ai" : ""}`} data-screen-label="Base de connaissance">
      {Side()}
      <main className="kb-main">
        {view.t === "folder" && folder ? (
          <KbFolder
            key={folder.id}
            f={folder}
            db={db}
            api={api}
            fav={isFav(folder.id)}
            onBack={() => setView({ t: "list" })}
            flash={flash}
            clients={clients}
            nomClient={nomClient}
            onAsk={askAi}
          />
        ) : view.t === "activity" ? (
          <Activity />
        ) : (
          List()
        )}
      </main>
      {ai && (
        <ProcAssistant
          cfg={{
            corpus: "kb",
            cle: "myalfa_kb_ai_v1",
            titre: "Assistant de recherche",
            sousTitre: `Répond à partir des ${db.folders.length} dossiers de la base`,
            intro: "Posez une question métier. L'assistant cherche d'abord dans les recherches déjà consignées par le cabinet et cite les dossiers utilisés.",
            sugg: ["Avons-nous déjà étudié la ZFANG pour un agriculteur ?", "Que retenir sur la cession d'un bien en LMNP ?", "Quels dossiers ne sont pas encore validés par un E-C ?"],
            note: "Vérifiez les sources citées avant toute utilisation.",
          }}
          contexte={folder ? { id: folder.id, titre: folder.title } : null}
          initialAsk={aiAsk}
          onOpen={(src) => open(Number(src.id))}
          onClose={() => setAi(false)}
        />
      )}
      {!ai && <ProcMascot onOpen={() => setAi(true)} bubble="Une recherche à mener ? Je fouille la base pour vous." />}
      {newOpen && (
        <KbNewModal
          init={typeof newOpen === "string" ? newOpen : ""}
          onClose={() => setNewOpen(false)}
          onCreate={async (d) => {
            const id = await api.create(d);
            if (!id) return;
            setNewOpen(false);
            setQ("");
            open(id);
            flash("Dossier créé");
          }}
        />
      )}
      {toast && (
        <div className="pr-toast">
          <Icon name="check" size={14} /> {toast}
        </div>
      )}
    </div>
  );
};

export default KnowledgeBase;
